/**
 * Scroll-driven WebGL Hero Animation
 * Replicates breakthroughenergy.org concentric-ring hero.
 * Dependencies: Three.js (global THREE), GSAP 3 + ScrollTrigger
 */
(function () {
  'use strict';

  var section = document.getElementById('hero-webgl-section');
  if (!section) return;
  if (typeof THREE === 'undefined' || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn('[hero-webgl] Three.js, GSAP, or ScrollTrigger not loaded — skipping.');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  /* ═══════════════════════════════════════════════════════
     CONFIGURATION
     ═══════════════════════════════════════════════════════ */
  var BG = 0x0F1125;
  var LIME = 0xB0CCE4;
  var LIME_CSS = '#A0B8D0';
  var RING_GRAY = 0x3D7EB9;
  var DOT_GRAY = 0xA0B8D0;
  var AXIS_GRAY = 0x999999;
  var FRUSTUM = 14;
  var SCROLL_PX = 2500;

  var BLUE_BG = 0x0F1125;
  var BLUE_RING = 0xA8BDD2;
  var BLUE_CENTER = 0x5B7DAA;
  var BLUE_HALO = 0x8AAAC8;
  var BLUE_DOT = 0xA0B8D0;
  var BLUE_DOT_LG = 0xB0CCE4;

  var mq768 = window.matchMedia('(max-width: 768px)');
  var mobile = mq768.matches;
  var tablet = !mobile && window.matchMedia('(max-width: 1024px)').matches;

  var isRTL = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
  var RING_RADII = mobile ? [1.2, 1.65, 2.1, 2.55] : tablet ? [1.5, 2.1, 2.7, 3.3] : [1.8, 2.5, 3.2, 3.9];
  var CX = mobile ? 0 : tablet ? (isRTL ? -3.5 : 3.5) : (isRTL ? -6.5 : 6.5);
  var CY = mobile ? -1.68 : tablet ? -0.8 : 0.3;

  var PARTICLE_DEFS = [
    { ring: 3, a: 1.85, s: 0.30, lime: 1, spd: 0.70 },
    { ring: 2, a: 4.30, s: 0.22, lime: 1, spd: -0.55 },
    { ring: 3, a: 0.40, s: 0.16, lime: 1, spd: 0.45 },
    { ring: 1, a: 2.70, s: 0.13, lime: 1, spd: -0.80 },
    { ring: 3, a: 5.20, s: 0.26, lime: 1, spd: 0.30 },
    { ring: 0, a: 0.30, s: 0.05, lime: 0, spd: 0.35 },
    { ring: 1, a: 5.60, s: 0.04, lime: 0, spd: -0.65 },
    { ring: 2, a: 1.10, s: 0.04, lime: 0, spd: 0.50 }
  ];
  if (mobile) PARTICLE_DEFS = PARTICLE_DEFS.slice(0, 5);

  /* ═══════════════════════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════════════════════ */
  function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function ss(lo, hi, x) {
    var t = clamp((x - lo) / (hi - lo), 0, 1);
    return t * t * (3 - 2 * t);
  }

  function ringGeo(radius, segs) {
    segs = segs || (mobile ? 80 : 128);
    var pts = [];
    for (var i = 0; i <= segs; i++) {
      var angle = (i / segs) * Math.PI * 2;
      pts.push(new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius, 0));
    }
    return new THREE.BufferGeometry().setFromPoints(pts);
  }

  function disc(r, col, segs) {
    var geo = new THREE.CircleGeometry(r, segs || 64);
    var mat = new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 1, depthWrite: false });
    return new THREE.Mesh(geo, mat);
  }

  function glowTexture(res) {
    var c = document.createElement('canvas');
    c.width = c.height = res;
    var g = c.getContext('2d');
    var gr = g.createRadialGradient(res / 2, res / 2, 0, res / 2, res / 2, res / 2);
    gr.addColorStop(0, 'rgba(255,255,255,0.55)');
    gr.addColorStop(0.30, 'rgba(255,255,255,0.25)');
    gr.addColorStop(0.65, 'rgba(255,255,255,0.08)');
    gr.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = gr;
    g.fillRect(0, 0, res, res);
    return new THREE.CanvasTexture(c);
  }

  /* ═══════════════════════════════════════════════════════
     THREE.JS SETUP
     ═══════════════════════════════════════════════════════ */
  var canvas = document.getElementById('hero-webgl-canvas');
  if (!canvas) return;

  var renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: !mobile,
    alpha: false,
    powerPreference: 'high-performance'
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

  var scene = new THREE.Scene();
  scene.background = new THREE.Color(BG);

  var canvasWrap = canvas.parentElement || section;
  var w = canvasWrap.clientWidth;
  var h = canvasWrap.clientHeight;
  var aspect = w / h;

  var camera = new THREE.OrthographicCamera(
    -FRUSTUM * aspect / 2, FRUSTUM * aspect / 2,
    FRUSTUM / 2, -FRUSTUM / 2,
    0.1, 100
  );
  camera.position.z = 10;
  renderer.setSize(w, h);

  /* ═══════════════════════════════════════════════════════
     SCENE ELEMENTS
     ═══════════════════════════════════════════════════════ */

  var world = new THREE.Group();
  world.position.set(CX, CY, 0);
  scene.add(world);

  // --- Glow sprites (neutral white texture, tinted by material color) ---
  var gTex = glowTexture(256);
  var glowInner = new THREE.Sprite(new THREE.SpriteMaterial({ map: gTex, color: LIME, transparent: true, opacity: 0, depthWrite: false }));
  var glowOuter = new THREE.Sprite(new THREE.SpriteMaterial({ map: gTex, color: LIME, transparent: true, opacity: 0, depthWrite: false }));
  glowInner.renderOrder = 0;
  glowOuter.renderOrder = 0;
  glowInner.scale.set(1, 1, 1);
  glowOuter.scale.set(1, 1, 1);
  world.add(glowOuter);
  world.add(glowInner);

  // --- Cross-highlight bands ---
  var crossHMat = new THREE.MeshBasicMaterial({ color: 0xD0E0F0, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false });
  var crossVMat = new THREE.MeshBasicMaterial({ color: 0xD0E0F0, transparent: true, opacity: 0, side: THREE.DoubleSide, depthWrite: false });
  var crossH = new THREE.Mesh(new THREE.PlaneGeometry(26, 0.22), crossHMat);
  var crossV = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 20), crossVMat);
  crossH.renderOrder = 1;
  crossV.renderOrder = 1;
  world.add(crossH);
  world.add(crossV);

  // --- Concentric rings ---
  var rings = RING_RADII.map(function (r) {
    var mat = new THREE.LineBasicMaterial({ color: RING_GRAY, transparent: true, opacity: 0.50, depthWrite: false });
    var mesh = new THREE.Line(ringGeo(r), mat);
    mesh.renderOrder = 2;
    world.add(mesh);
    return { mesh: mesh, mat: mat, baseR: r };
  });

  // --- Orbiting particles ---
  var particles = PARTICLE_DEFS.map(function (d) {
    var m = disc(1, d.lime ? LIME : DOT_GRAY);
    m.scale.set(d.s, d.s, 1);
    m.renderOrder = 3;
    var r = RING_RADII[d.ring];
    m.position.set(Math.cos(d.a) * r, Math.sin(d.a) * r, 0);
    world.add(m);
    return { mesh: m, cfg: d, baseR: r };
  });

  // --- Central circle ---
  var central = disc(1, LIME);
  central.scale.set(0.06, 0.06, 1);
  central.renderOrder = 4;
  world.add(central);

  // --- Halo ring (lighter circle around central, used in blue end-state) ---
  var halo = disc(1, BLUE_HALO);
  halo.material.opacity = 0;
  halo.renderOrder = 3;
  world.add(halo);

  // --- Inner concentric layers (mid-scroll reveal, fade before blue transition) ---
  var innerLime1 = disc(1, 0x8AAAC8);
  innerLime1.material.opacity = 0;
  innerLime1.renderOrder = 5;
  world.add(innerLime1);

  var innerLime2 = disc(1, 0x9AB8D0);
  innerLime2.material.opacity = 0;
  innerLime2.renderOrder = 6;
  world.add(innerLime2);

  var innerGray = disc(1, 0xD6D5CE);
  innerGray.material.opacity = 0;
  innerGray.renderOrder = 7;
  world.add(innerGray);

  // --- Axis line + ticks ---
  var axisMat = new THREE.LineBasicMaterial({ color: AXIS_GRAY, transparent: true, opacity: 0, depthWrite: false });
  var axisH = new THREE.Line(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-0.3, 0, 0),
      new THREE.Vector3(8.5, 0, 0)
    ]),
    axisMat
  );
  axisH.renderOrder = 8;
  world.add(axisH);

  var tickMats = [];
  for (var ti = 0; ti < 6; ti++) {
    var tMat = new THREE.LineBasicMaterial({ color: AXIS_GRAY, transparent: true, opacity: 0, depthWrite: false });
    var yOff = 3.6 - ti * 1.44;
    var tLine = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([
        new THREE.Vector3(7.0, yOff, 0),
        new THREE.Vector3(7.3, yOff, 0)
      ]),
      tMat
    );
    tLine.renderOrder = 8;
    world.add(tLine);
    tickMats.push(tMat);
  }

  /* ═══════════════════════════════════════════════════════
     COLOR OBJECTS (pre-created for efficient per-frame lerping)
     ═══════════════════════════════════════════════════════ */
  var cBgA = new THREE.Color(BG);
  var cBgB = new THREE.Color(BLUE_BG);
  var cRingA = new THREE.Color(RING_GRAY);
  var cRingB = new THREE.Color(BLUE_RING);
  var cLimeA = new THREE.Color(LIME);
  var cCenterB = new THREE.Color(BLUE_CENTER);
  var cHaloB = new THREE.Color(BLUE_HALO);
  var cDotGrayA = new THREE.Color(DOT_GRAY);
  var cDotB = new THREE.Color(BLUE_DOT);
  var cDotLgB = new THREE.Color(BLUE_DOT_LG);
  var cIl1A = new THREE.Color(0x8AAAC8);

  /* ═══════════════════════════════════════════════════════
     SCROLL + MOUSE STATE
     ═══════════════════════════════════════════════════════ */
  var progress = 0;
  var mNX = 0, mNY = 0, smX = 0, smY = 0;

  /* ═══════════════════════════════════════════════════════
     UPDATE SCENE (runs every frame, driven by scroll progress)
     ═══════════════════════════════════════════════════════ */
  function update(p) {
    var expansion = 1 + p * 0.65;
    // Tighten ring spacing near the end so outer gaps do not look too large.
    var compactExpansion = 1 + p * 0.20;
    var ringExpansion = lerp(expansion, compactExpansion, ss(0.76, 1.0, p));

    // Color-transition factor: 0 = lime/cream, 1 = blue
    var ct = ss(0.52, 0.92, p);

    // Background: cream → light blue
    scene.background.lerpColors(cBgA, cBgB, ct);

    /* --- Rings: expand, fade mid-scroll, recover at end in blue --- */
    rings.forEach(function (r, i) {
      r.mesh.scale.set(ringExpansion, ringExpansion, 1);
      r.mat.color.lerpColors(cRingA, cRingB, ct);
      var op;
      switch (i) {
        case 0:
          op = lerp(0.48, 0.12, ss(0.20, 0.50, p));
          op = lerp(op, 0.48, ss(0.58, 0.88, p));
          break;
        case 1:
          op = lerp(0.46, 0.0, ss(0.18, 0.46, p));
          op = lerp(op, 0.44, ss(0.55, 0.85, p));
          break;
        case 2:
          op = lerp(0.42, 0.0, ss(0.10, 0.38, p));
          op = lerp(op, 0.42, ss(0.55, 0.82, p));
          break;
        case 3:
          op = lerp(0.38, 0.12, ss(0.04, 0.28, p));
          op = lerp(op, 0.46, ss(0.50, 0.80, p));
          break;
      }
      r.mat.opacity = op;
    });

    /* --- Particles: orbit, grow, fade mid, recover blue at end --- */
    particles.forEach(function (pa) {
      var c = pa.cfg;
      var r = pa.baseR * ringExpansion;
      var angle = c.a + p * c.spd * Math.PI * 1.6;
      pa.mesh.position.x = Math.cos(angle) * r;
      pa.mesh.position.y = Math.sin(angle) * r;

      var sm = 1 + ss(0.12, 0.50, p) * 0.4;
      var s = c.s * sm;
      pa.mesh.scale.set(s, s, 1);

      if (c.lime) {
        pa.mesh.material.color.lerpColors(cLimeA, cDotLgB, ct);
      } else {
        pa.mesh.material.color.lerpColors(cDotGrayA, cDotB, ct);
      }

      var opFade;
      if (c.ring >= 2) {
        opFade = lerp(1.0, 0.08, ss(0.28, 0.55, p));
        opFade = lerp(opFade, 0.85, ss(0.58, 0.88, p));
      } else {
        opFade = lerp(1.0, 0.05, ss(0.40, 0.62, p));
        opFade = lerp(opFade, 0.80, ss(0.62, 0.90, p));
      }
      pa.mesh.material.opacity = opFade;
    });

    /* --- Central circle: grow mid-scroll, settle small, turn blue --- */
    var cS;
    if (p < 0.55) {
      cS = lerp(0.06, 1.4, ss(0.06, 0.55, p));
    } else {
      cS = lerp(1.4, 0.9, ss(0.55, 0.95, p));
    }
    central.scale.set(cS, cS, 1);
    central.material.opacity = ss(0.02, 0.14, p);
    central.material.color.lerpColors(cLimeA, cCenterB, ct);

    /* --- Halo ring (lighter blue around central, appears in blue phase) --- */
    var haloS = cS * 1.35;
    halo.scale.set(haloS, haloS, 1);
    halo.material.opacity = ss(0.58, 0.82, p) * 0.55;
    halo.material.color.lerpColors(cIl1A, cHaloB, ct);

    /* --- Glow: tinted, follows central --- */
    var g1s = cS * 1.2;
    glowInner.scale.set(g1s, g1s, 1);
    glowInner.material.opacity = ss(0.10, 0.35, p) * 0.35 * lerp(1.0, 0.6, ct);
    glowInner.material.color.lerpColors(cLimeA, cHaloB, ct);

    var g2s = cS * 1.8;
    glowOuter.scale.set(g2s, g2s, 1);
    glowOuter.material.opacity = ss(0.14, 0.40, p) * 0.12 * lerp(1.0, 0.5, ct);
    glowOuter.material.color.lerpColors(cLimeA, cHaloB, ct);

    /* --- Cross highlight: fades away before blue phase --- */
    var crossOp = ss(0.18, 0.42, p) * 0.06 * (1 - ss(0.52, 0.78, p));
    crossHMat.opacity = crossOp;
    crossVMat.opacity = crossOp;
    var crossThick = lerp(0.22, 0.8, ss(0.20, 0.50, p));
    crossH.scale.y = crossThick / 0.22;
    crossV.scale.x = crossThick / 0.22;

    /* --- Inner concentric layers: appear mid-scroll, fade before blue phase --- */
    var layerFade = 1 - ss(0.60, 0.85, p);

    var il1s = lerp(0.01, 2.0, ss(0.38, 0.62, p));
    innerLime1.scale.set(il1s, il1s, 1);
    innerLime1.material.opacity = ss(0.38, 0.52, p) * 0.50 * layerFade;
    innerLime1.material.color.lerpColors(cIl1A, cHaloB, ct);

    var il2s = lerp(0.01, 1.4, ss(0.42, 0.65, p));
    innerLime2.scale.set(il2s, il2s, 1);
    innerLime2.material.opacity = ss(0.42, 0.56, p) * 0.38 * layerFade;

    var grayS = lerp(0.01, 0.8, ss(0.48, 0.68, p));
    innerGray.scale.set(grayS, grayS, 1);
    innerGray.material.opacity = ss(0.48, 0.60, p) * 0.45 * layerFade;

    /* --- Axis + ticks: appear mid, fade before blue phase --- */
    var axOp = ss(0.50, 0.68, p) * 0.10 * (1 - ss(0.72, 0.90, p));
    axisMat.opacity = axOp;
    tickMats.forEach(function (tm) { tm.opacity = axOp; });

    /* --- Mouse parallax --- */
    smX += (mNX - smX) * 0.05;
    smY += (mNY - smY) * 0.05;
    world.position.x = CX + smX * 0.35;
    world.position.y = CY + smY * 0.20;
  }

  /* ═══════════════════════════════════════════════════════
     GSAP TIMELINE + SCROLLTRIGGER
     ═══════════════════════════════════════════════════════ */
  var slides = section.querySelectorAll('.hero-webgl-slide');
  var dots = section.querySelectorAll('.hero-webgl-dot');
  var discover = section.querySelector('.hero-webgl-discover');
  var labels = section.querySelectorAll('.hero-webgl-label');
  var centerLogo = section.querySelector('.hero-webgl-center-logo');
  var canvasWrap = section.querySelector('.hero-webgl-canvas-wrap');

  /** Match CSS: desktop slides use top:50% + translateY(-50%); ≤1024px use normal flow */
  function slideAlign() {
    return window.matchMedia('(min-width: 1025px)').matches ? { yPercent: -50 } : { yPercent: 0 };
  }

  /** Scroll-step motion: outgoing moves up; incoming rises from below (vh keeps it consistent across breakpoints) */
  var EXIT_Y = '-15vh';
  var ENTER_Y = '18vh';
  var VISUAL_DRIFT = '7vh';

  var tl = gsap.timeline({
    scrollTrigger: {
      trigger: section,
      start: 'top top',
      end: '+=' + (mobile ? 1800 : SCROLL_PX),
      pin: true,
      scrub: 0.6,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: function (self) { progress = self.progress; }
    }
  });

  function pairVisualScroll(exitAt, enterAt) {
    if (canvasWrap) {
      tl.to(canvasWrap, { y: '-' + VISUAL_DRIFT, duration: 0.10, ease: 'power2.in' }, exitAt);
      tl.fromTo(canvasWrap, { y: VISUAL_DRIFT }, { y: 0, duration: 0.12, ease: 'power2.out' }, enterAt);
    }
    if (centerLogo) {
      tl.to(centerLogo, { y: '-' + VISUAL_DRIFT, duration: 0.10, ease: 'power2.in' }, exitAt);
      tl.fromTo(centerLogo, { y: VISUAL_DRIFT }, { y: 0, duration: 0.12, ease: 'power2.out' }, enterAt);
    }
  }

  /* Initial slide + stack states (avoids flashes before first tween) */
  if (slides.length) {
    for (var sj = 1; sj < slides.length; sj++) {
      gsap.set(slides[sj], Object.assign({ opacity: 0, y: ENTER_Y }, slideAlign()));
    }
    if (slides[0]) gsap.set(slides[0], Object.assign({ opacity: 1, y: 0 }, slideAlign()));
  }
  if (canvasWrap) gsap.set(canvasWrap, { y: 0 });
  if (centerLogo) {
    gsap.set(centerLogo, Object.assign({ opacity: 1, scale: 1, xPercent: -50, yPercent: -50, y: 0 }));
  }

  /* Slide 1 → 2: text exits upward; slide 2 scrubs in from below; canvas + logo match */
  var t12Exit = 0.18;
  var t12Enter = 0.24;
  if (slides[0]) {
    tl.to(slides[0], { opacity: 0, y: EXIT_Y, duration: 0.12, ease: 'power2.in' }, t12Exit);
  }
  if (slides[1]) {
    tl.fromTo(
      slides[1],
      Object.assign({ opacity: 0, y: ENTER_Y }, slideAlign()),
      Object.assign({ opacity: 1, y: 0, duration: 0.13, ease: 'power2.out' }, slideAlign()),
      t12Enter
    );
  }
  if (slides[0] && slides[1]) pairVisualScroll(t12Exit, t12Enter);

  /* Slide 2 → 3 */
  var t23Exit = 0.46;
  var t23Enter = 0.62;
  if (slides[1]) {
    tl.to(slides[1], { opacity: 0, y: EXIT_Y, duration: 0.12, ease: 'power2.in' }, t23Exit);
  }
  if (slides[2]) {
    tl.fromTo(
      slides[2],
      Object.assign({ opacity: 0, y: ENTER_Y }, slideAlign()),
      Object.assign({ opacity: 1, y: 0, duration: 0.13, ease: 'power2.out' }, slideAlign()),
      t23Enter
    );
  }
  if (slides[1] && slides[2]) pairVisualScroll(t23Exit, t23Enter);

  /* DISCOVER label */
  if (discover) {
    tl.fromTo(discover, { opacity: 0 }, { opacity: 1, duration: 0.08 }, 0.08);
    tl.to(discover, { opacity: 0, duration: 0.06 }, t12Enter + 0.02);
  }

  /* Center logo: visible at start, then fades while center transitions */
  if (centerLogo) {
    tl.to(centerLogo, { opacity: 0, scale: 0.88, duration: 0.10, ease: 'power2.inOut' }, 0.42);
  }

  /* Axis labels: appear mid-scroll, fade before blue */
  if (labels.length) {
    for (var li = 0; li < labels.length; li++) {
      tl.fromTo(labels[li], { opacity: 0 }, { opacity: 1, duration: 0.10 }, 0.52);
      tl.to(labels[li], { opacity: 0, duration: 0.08 }, 0.74);
    }
  }

  /* Progress dots — align with when each slide settles */
  if (dots.length >= 3) {
    gsap.set(dots[0], { backgroundColor: LIME_CSS });
    tl.to(dots[1], { backgroundColor: LIME_CSS, duration: 0.01 }, t12Enter + 0.04);
    tl.to(dots[2], { backgroundColor: LIME_CSS, duration: 0.01 }, t23Enter + 0.04);
  }

  /* ═══════════════════════════════════════════════════════
     RENDER LOOP (synced with GSAP ticker → 60 fps)
     ═══════════════════════════════════════════════════════ */
  function positionCenterLogo() {
    if (!centerLogo) return;
    var tempV = new THREE.Vector3(world.position.x, world.position.y, 0);
    tempV.project(camera);
    var x = (tempV.x * 0.5 + 0.5) * w;
    var y = (tempV.y * -0.5 + 0.5) * h;
    centerLogo.style.left = x + 'px';
    centerLogo.style.top = y + 'px';
  }

  function render() {
    update(progress);
    positionCenterLogo();
    renderer.render(scene, camera);
  }
  gsap.ticker.add(render);

  /* ═══════════════════════════════════════════════════════
     MOUSE PARALLAX
     ═══════════════════════════════════════════════════════ */
  function onPointerMove(e) {
    var rect = section.getBoundingClientRect();
    mNX = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    mNY = -((e.clientY - rect.top) / rect.height - 0.5) * 2;
  }
  section.addEventListener('mousemove', onPointerMove);
  section.addEventListener('touchmove', function (e) {
    if (e.touches && e.touches.length) onPointerMove(e.touches[0]);
  }, { passive: true });
  section.addEventListener('mouseleave', function () { mNX = mNY = 0; });

  /* ═══════════════════════════════════════════════════════
     RESIZE
     ═══════════════════════════════════════════════════════ */
  var rTimer;
  function onResize() {
    clearTimeout(rTimer);
    rTimer = setTimeout(function () {
      var canvasWrap = canvas.parentElement || section;
      w = canvasWrap.clientWidth;
      h = canvasWrap.clientHeight;
      aspect = w / h;
      camera.left = -FRUSTUM * aspect / 2;
      camera.right = FRUSTUM * aspect / 2;
      camera.top = FRUSTUM / 2;
      camera.bottom = -FRUSTUM / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      mobile = mq768.matches;
      var resTablet = !mobile && window.matchMedia('(max-width: 1024px)').matches;
      var isRTL = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
      CX = mobile ? 0 : resTablet ? (isRTL ? -3.5 : 3.5) : (isRTL ? -6.5 : 6.5);
      CY = mobile ? -1.68 : resTablet ? -0.8 : 0.3;
      ScrollTrigger.refresh();
      positionCenterLogo();
    }, 150);
  }
  window.addEventListener('resize', onResize);

})();
